import React, { useState, useEffect } from 'react';

export function getInitials(
  name?: string,
  firstName?: string,
  lastName?: string
): string {
  if (firstName !== undefined && lastName !== undefined) {
    const f = (firstName || '').trim();
    const l = (lastName || '').trim();
    if (f && l) return (f[0] + l[0]).toUpperCase();
    if (f) return f.slice(0, 2).toUpperCase();
    if (l) return l.slice(0, 2).toUpperCase();
  }
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  return '';
}

const sizeClasses = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-16 h-16 text-lg',
} as const;

export interface AvatarProps {
  src?: string | null;
  name?: string;
  firstName?: string;
  lastName?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  alt?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  firstName,
  lastName,
  size = 'md',
  className = '',
  alt,
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(src) && !imageFailed;
  const initials = getInitials(name, firstName, lastName);
  const sizeClass = sizeClasses[size];

  // When src changes, reset so we try loading the new image (e.g. user fixed the URL)
  useEffect(() => {
    setImageFailed(false);
  }, [src]);

  if (showImage) {
    return (
      <img
        src={src!}
        alt={alt ?? (initials || 'Avatar')}
        className={`rounded-full object-cover flex-shrink-0 ${sizeClass} ${className}`.trim()}
        onError={() => setImageFailed(true)}
      />
    );
  }

  return (
    <div
      className={`rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-gray-600 bg-gray-200 ${sizeClass} ${className}`.trim()}
      aria-hidden={!initials}
    >
      {initials || '?'}
    </div>
  );
};
